import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../context/RTLContext';
import ThemeToggle from '../UI/ThemeToggle';

const HOVER_OUT_GRACE_MS = 250;
const PIN_STORAGE_KEY = 'sidebar_pinned';

const Sidebar = ({ isOpen, onClose, isCollapsed, setIsCollapsed, isHovered, setIsHovered }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const location = useLocation();
  const hoverOutTimerRef = useRef(null);

  const [isPinned, setIsPinned] = useState(() => {
    try {
      return localStorage.getItem(PIN_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Honor pinned preference on mount and whenever pin toggles
  useEffect(() => {
    setIsCollapsed(!isPinned);
  }, [isPinned, setIsCollapsed]);

  // Clean up any pending hover-out timer on unmount
  useEffect(() => {
    return () => {
      if (hoverOutTimerRef.current) {
        clearTimeout(hoverOutTimerRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (hoverOutTimerRef.current) {
      clearTimeout(hoverOutTimerRef.current);
      hoverOutTimerRef.current = null;
    }
    setIsHovered(true);
  }, [setIsHovered]);

  const handleMouseLeave = useCallback(() => {
    if (isPinned) return;
    if (hoverOutTimerRef.current) clearTimeout(hoverOutTimerRef.current);
    hoverOutTimerRef.current = setTimeout(() => {
      setIsHovered(false);
      hoverOutTimerRef.current = null;
    }, HOVER_OUT_GRACE_MS);
  }, [isPinned, setIsHovered]);

  const togglePin = useCallback(() => {
    setIsPinned(prev => {
      const next = !prev;
      try {
        localStorage.setItem(PIN_STORAGE_KEY, String(next));
      } catch {
        // ignore quota / private mode
      }
      return next;
    });
  }, []);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const showCompact = isCollapsed && !isHovered && !isPinned;

  const menuItems = [
    {
      id: 'overview',
      label: t('navigation.overview'),
      path: '/dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    },
    {
      id: 'search',
      label: t('navigation.queries'),
      path: '/search',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      )
    },
    {
      id: 'settings',
      label: t('navigation.settings'),
      path: '/dashboard/settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    // Admin menu item (only shown for admin users)
    ...(isAdmin ? [{
      id: 'admin',
      label: t('navigation.admin'),
      path: '/admin',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }] : [])
  ];

  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path !== '/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const pinLabel = isPinned ? t('navigation.unpinSidebar', 'Unpin sidebar') : t('navigation.pinSidebar', 'Pin sidebar');

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="modern-modal-overlay lg:hidden"
          onClick={onClose}
          style={{ position: 'fixed', zIndex: 40 }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          modern-sidebar fixed top-0 h-full z-50
          ${isRTL ? 'right-0' : 'left-0'}
          ${showCompact ? 'w-20' : 'w-72'}
          ${isOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0')}
        `}
        style={{
          transition: 'width 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.3s ease',
          willChange: 'width'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="h-full flex flex-col">
          {/* Logo + Pin/Close Header */}
          <div className="modern-sidebar-header flex items-center justify-between">
            <div className={`flex items-center ${showCompact ? 'justify-center w-full' : 'justify-start'}`}>
              <img
                src="/brandon-no-bg.svg"
                alt="Brandon"
                className={showCompact ? 'h-8 w-auto' : 'h-12 w-auto'}
                style={{
                  transition: 'height 0.4s ease, opacity 0.4s ease, transform 0.4s ease',
                  opacity: 1,
                  transform: 'scale(1)'
                }}
              />
            </div>
            {/* Pin toggle - desktop only, visible when expanded */}
            {!showCompact && (
              <button
                onClick={togglePin}
                className="modern-btn-icon-sm modern-btn-ghost hidden lg:inline-flex focus:outline-none focus:ring-2 focus:ring-primary/40"
                title={pinLabel}
                aria-label={pinLabel}
                aria-pressed={isPinned}
              >
                <svg
                  className={`w-5 h-5 transition-transform ${isPinned ? 'rotate-0 text-primary' : 'rotate-45'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v6m0 0l3 3m-3-3l-3 3m-1 8h8a1 1 0 001-1v-3a4 4 0 00-4-4h-2a4 4 0 00-4 4v3a1 1 0 001 1z" />
                </svg>
              </button>
            )}
            {/* Mobile close button */}
            <button
              onClick={onClose}
              className="modern-btn-icon-sm modern-btn-ghost lg:hidden"
              aria-label={t('common.buttons.close', 'Close')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* User Profile Section */}
          {user && (
            <div className={`pb-6 ${showCompact ? 'px-4' : 'px-6'}`}>
              <div className={`flex items-center ${showCompact ? 'justify-center' : 'gap-3'}`}>
                <div
                  className="flex items-center justify-center rounded-full font-bold text-sm flex-shrink-0"
                  style={{
                    backgroundColor: '#d8f9b8',
                    color: '#2b2d2d',
                    width: '48px',
                    height: '48px',
                    minWidth: '48px',
                    minHeight: '48px'
                  }}
                >
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </div>
                <div
                  className="flex-1 min-w-0"
                  style={{
                    opacity: showCompact ? 0 : 1,
                    transform: showCompact ? 'translateX(-10px)' : 'translateX(0)',
                    transition: 'opacity 0.4s ease, transform 0.4s ease',
                    pointerEvents: showCompact ? 'none' : 'auto',
                    display: showCompact ? 'none' : 'block'
                  }}
                >
                  <p className="text-sm font-semibold truncate text-gray-800 dark:text-gray-100">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs truncate text-gray-600 dark:text-gray-400">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="modern-sidebar-nav">
            {menuItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => isOpen && onClose()}
                className={`modern-nav-item ${isActive(item.path) ? 'active' : ''} ${isRTL ? 'rtl-nav-item' : ''}`}
                title={showCompact ? item.label : ''}
              >
                <span className={`nav-icon ${isRTL ? 'rtl-icon' : ''}`}>{item.icon}</span>
                <span
                  className={isRTL ? 'rtl-text' : ''}
                  style={{
                    opacity: showCompact ? 0 : 1,
                    width: showCompact ? 0 : 'auto',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    transition: 'opacity 0.4s ease, width 0.4s ease'
                  }}
                >
                  {item.label}
                </span>
              </Link>
            ))}
          </nav>

          {/* Dark Mode Toggle */}
          <div className="mt-auto px-6 pb-6">
            <div className={`flex ${showCompact ? 'justify-center' : 'justify-start'}`}>
              <ThemeToggle className="w-full" />
            </div>
          </div>

        </div>
      </aside>
    </>
  );
};

export default Sidebar;
